import {createContext, useContext} from "react";
import * as Navigator from "../refrences/RootNavigation";
import * as Navigation from "../refrences/RootNavigation";
import {MenuDetails} from "../rest/jtmc/models/MenuDetails";
import AppConfigContext from "./AppConfigContext";
import {CartContext} from "./CartContext";
import DeliveryAddress from "../rest/jtmc/models/DeliveryAddress";
import ClientInfo from "../rest/jtmc/models/ClientInfo";
import Game from "../rest/jtmc/models/Game";
import {askNotificationAccess, openUrlWithIpgCallbackChannelSubscribe} from "../helpers";
import {subscribeUser} from "../src/pushScripts";
import {storageFunctions} from "../storage";
import Service from "../rest/jtmc/models/Service";
import {ServiceContext} from "./ServicesContext";
import OrderAction from "../rest/jtmc/models/OrderAction";
import IPGLinkResponse from "../rest/jtmc/models/IPGLinkResponse";
import {OrderContext} from "./OrderContext";

export const AppFunctionsContext = createContext({} as ContextValues);

type ContextValues = {
    appNavigator: {
        toLoginPage: () => void,
        toOrderProgress: (menuDetails: MenuDetails) => void,
        toIPGCompletion: () => void,
        toCTCReceipt: () => void,
        toUserMenu: () => void,
        toServices: () => void,
        toGames: () => void,
        toCart: () => void,
        cartToNextPage: () => void,
        toAddAddress: () => void,
        toOverviewFromDelivery: (del: DeliveryAddress | null) => void,
        toAddressEdit: (del: DeliveryAddress) => void,
        toMenu: () => void,
        back: () => void,
        toGameScreen: (game: Game) => void,
        toClientInfoEdit: () => void,
        toOrdersHistory: () => void,
        toDeliveryAddresses: () => void,
        toTakeawayReceipt: (menuDetails: MenuDetails) => void,
    },
    appFetch: {
        getMenuDetails: () => Promise<MenuDetails>,
        getUserAddresses: () => Promise<DeliveryAddress[]>,
        getClientInfo: () => Promise<ClientInfo>
    },
    functions: {
        waiterCall: () => void,
        openTellUrl: (tell: string) => void,
        setClientName: (t: string) => Promise<void>,
        askNotificationAccess: () => void,
        cancelNotificationOnModal: () => void,
        requestService: (service: Service) => void,
        finalizeOrder: (selectedPaymentMethod: "ipg" | "ctc" | "pos" | null, description: string) => void,
        goToCartPage: (menuDetails: MenuDetails) => void,
        openManualPWAInstallPrompt: () => void
    }
}


export function AppFunctionsProvider({children}: any) {
    const appConfigContext = useContext(AppConfigContext);
    const fetchContext = appConfigContext.fetch;
    const cartContext = useContext(CartContext);
    const servicesContext = useContext(ServiceContext);
    const orderContext = useContext(OrderContext);
    const snacks = appConfigContext.snackBarData?.events;

    return <AppFunctionsContext.Provider value={{
        appNavigator: {
            toLoginPage: () => {
                Navigator.replace("Login");
            },
            toOrderProgress: (menuDetails: MenuDetails) => {
                Navigator.replace("OrderProgress", {
                    menuDetails: menuDetails
                });
            },
            toIPGCompletion: () => {
                Navigator.navigate("IPGCompletion");
            },
            toCTCReceipt: () => {
                Navigator.navigate("CTCReceipt");
            },
            toUserMenu: () => {
                Navigator.navigate("UserMenu");
            },
            toServices: () => {
                Navigator.navigate("Services");
            },
            toGames: () => {
                Navigator.navigate("Games");
            },
            toCart: () => {
                Navigator.navigate("Cart");
            },
            cartToNextPage: () => {
                let nextPage = "Delivery";
                switch (appConfigContext.orderType) {
                    case "table":
                    case "takeaway":
                        nextPage = "Overview";
                        break;
                    case "outside":
                    default:
                        break;
                }
                if (cartContext.cartItems.length != 0)
                    Navigator.navigate(nextPage);
                else snacks?.info("هیچ آیتمی انتخاب نشده است!");
            },
            toAddAddress: () => {
                Navigator.navigate("AddressEditor", {
                    addressModel: null
                });
            },
            toOverviewFromDelivery: (del: DeliveryAddress | null) => {
                if (del != null)
                    fetchContext.Order.requests.setAddress({id: del.id}).then(res => {
                        Navigator.navigate("Overview");
                    });
                else Navigator.navigate("Overview");
            },
            toAddressEdit: (del: DeliveryAddress) => {
                Navigator.navigate("AddressEditor", {
                    addressModel: del
                });
            },
            toMenu: () => {
                Navigator.navigate("Menu");
            },
            back: () => {
                Navigator.goBack();
            },
            toGameScreen: (game) => {
                Navigator.navigate("GameScreen", {
                    game: game
                });
            },
            toClientInfoEdit: () => {
                Navigator.navigate("ClientInfoEdit");
            },
            toOrdersHistory: () => {
                Navigator.navigate("OrdersHistory");
            },
            toDeliveryAddresses: () => {
                Navigator.navigate("Delivery", {
                    selectable: false
                });
            },
            toTakeawayReceipt: (menuDetails) => {
                Navigator.replace("TakeawayReceipt", {
                    menuDetails: menuDetails
                });
            },
        },
        appFetch: {
            getMenuDetails: () => {
                return new Promise<MenuDetails>((resolve) => {
                    fetchContext.Menu.requests.getMenuDetails().then(res => {
                        resolve(res);
                    })
                })
            },
            getUserAddresses: () => {
                return new Promise<DeliveryAddress[]>((resolve) => {
                    fetchContext.Order.requests.getAddresses().then(addresses => {
                        resolve(addresses);
                    });
                });
            },
            getClientInfo: () => {
                return new Promise<ClientInfo>((resolve) => {
                    fetchContext.Auth.requests.clientInfo().then(info => {
                        resolve(info);
                    });
                });
            }
        },
        functions: {
            waiterCall: () => {
                fetchContext.Menu.requests.waiterCall().then(res => {
                    appConfigContext.snackBarData?.events?.success("گارسون در راه است")
                });
            },
            openTellUrl: (tell) => {
                open(`tel:${tell}`)
            },
            setClientName: (name: string) => {
                return new Promise((resolve) => {
                    fetchContext.Auth.requests.setName(name).then(res => {
                        Navigator.goBack();
                        resolve();
                    });
                })
            },
            askNotificationAccess: () => {
                askNotificationAccess().then(hasAccess => {
                    if (hasAccess)
                        subscribeUser();
                });
                Navigator.goBack();
            },
            cancelNotificationOnModal: () => {
                storageFunctions.saveData("notification_denied_on_modal", "true").then(() => {
                    Navigator.goBack();
                });
            },
            requestService: (service) => {
                servicesContext.request(service);
                Navigator.goBack();
            },
            finalizeOrder: (
                selectedPaymentMethod,
                description
            ) => {
                if (orderContext.order?.deliveryMethod == null) {
                    if (snacks?.info) snacks.info("روش تحویل را انتخاب کنید");
                    return;
                }
                if (selectedPaymentMethod == null) {
                    if (snacks?.info) snacks.info("روش پرداخت را انتخاب کنید");
                    return;
                }
                // checkItems
                fetchContext.Order.requests.finalize({
                    description: description,
                    payment_method: selectedPaymentMethod
                }).then((res: OrderAction) => {
                    switch (res.action) {
                        case "goto_ipg":
                            fetchContext.Order.requests.getIPGLink().then((res: IPGLinkResponse) => {
                                if (orderContext.order != null)
                                    openUrlWithIpgCallbackChannelSubscribe(res.url, orderContext.order?.id, () => {
                                        Navigator.navigate("Menu");
                                    }, () => {
                                        Navigator.navigate("Menu");
                                    });
                            });
                            break;
                        case "active_order_information_page":
                        case "show_branch_decline_page":
                        case "ctc_receipt_form":
                        case "show_delivery_decline_page":
                            Navigator.navigate("Menu");
                            break;
                    }
                });
            },
            goToCartPage: (menuDetails: MenuDetails) => {
                if (menuDetails.userIsGuestCantOrder) {
                    Navigator.navigate("GuestNotAllowed");
                    return;
                }
                Navigator.navigate("Cart");
            },
            openManualPWAInstallPrompt: () => {
                Navigation.navigate("PWAInstallPrompt", {
                    event: null,
                    origin: "manual"
                });
            }
        }
    }}>
        {children}
    </AppFunctionsContext.Provider>
}